import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { fetchData } from '../../helpers/apiUtils';
import { CAuthor, CBroker, CSite } from './types';

/**
 * Create a broker content feed — surfaces the existing `POST /api/broker-content/feeds`
 * (`createFeed`) so Christian can onboard a feed without an SQL/migration handoff. The backend
 * validates the URL(s) and enforces the unique `source_name`.
 */
interface FeedCreateDialogProps {
  open: boolean;
  brokers: CBroker[];
  authors: CAuthor[];
  sites: CSite[];
  onClose: () => void;
  onSaved: () => void;
}

const FeedCreateDialog: React.FC<FeedCreateDialogProps> = ({
  open,
  brokers,
  authors,
  sites,
  onClose,
  onSaved,
}) => {
  const [brokerId, setBrokerId] = useState<number | ''>('');
  const [label, setLabel] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [sourceUrlsText, setSourceUrlsText] = useState('');
  const [format, setFormat] = useState('syn_rss');
  const [sourceName, setSourceName] = useState('');
  const [defaultAuthorId, setDefaultAuthorId] = useState<number | ''>('');
  const [targetSites, setTargetSites] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setBrokerId('');
      setLabel('');
      setSourceUrl('');
      setSourceUrlsText('');
      setFormat('syn_rss');
      setSourceName('');
      setDefaultAuthorId('');
      setTargetSites([]);
      setNotes('');
      setError(null);
    }
  }, [open]);

  const toggleSite = (key: string) =>
    setTargetSites((prev) => (prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key]));

  const canSave =
    brokerId !== '' && label.trim() !== '' && sourceUrl.trim() !== '' && sourceName.trim() !== '';

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    const sourceUrls = sourceUrlsText
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    const res = await fetchData('/api/broker-content/feeds', {
      method: 'POST',
      body: JSON.stringify({
        broker_id: brokerId,
        label: label.trim(),
        source_url: sourceUrl.trim(),
        source_urls: sourceUrls.length ? sourceUrls : null,
        format,
        source_name: sourceName.trim(),
        default_author_id: defaultAuthorId === '' ? null : defaultAuthorId,
        target_sites: targetSites.length ? targetSites : null,
        notes: notes.trim() || null,
      }),
    });
    setSaving(false);
    if (res.success) onSaved();
    else setError(res.error || 'Speichern fehlgeschlagen');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Feed hinzufügen</DialogTitle>
      <DialogContent>
        {error && (
          <Typography color="error" sx={{ mb: 1 }}>
            {error}
          </Typography>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Broker</InputLabel>
            <Select
              label="Broker"
              value={brokerId}
              onChange={(e) => setBrokerId(e.target.value === '' ? '' : Number(e.target.value))}
            >
              {brokers.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            size="small"
            helperText="Anzeigename in der Feed-Liste."
          />

          <TextField
            label="Feed-URL"
            value={sourceUrl}
            onChange={(e) => setSourceUrl(e.target.value)}
            size="small"
          />

          <TextField
            label="Fallback-URLs (optional, eine pro Zeile)"
            value={sourceUrlsText}
            onChange={(e) => setSourceUrlsText(e.target.value)}
            multiline
            minRows={2}
            size="small"
            helperText="Erste erreichbare URL gewinnt."
          />

          <FormControl fullWidth size="small">
            <InputLabel>Format</InputLabel>
            <Select
              label="Format"
              value={format}
              onChange={(e) => setFormat(String(e.target.value))}
            >
              <MenuItem value="syn_rss">syn_rss (RSS / Syndication)</MenuItem>
              <MenuItem value="json">json</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Quell-ID (source_name, eindeutig)"
            value={sourceName}
            onChange={(e) => setSourceName(e.target.value)}
            size="small"
            helperText="Eindeutiger technischer Schlüssel, z. B. tickmill."
          />

          <FormControl fullWidth size="small">
            <InputLabel>Standard-Autor (optional)</InputLabel>
            <Select
              label="Standard-Autor (optional)"
              value={defaultAuthorId}
              onChange={(e) =>
                setDefaultAuthorId(e.target.value === '' ? '' : Number(e.target.value))
              }
            >
              <MenuItem value="">
                <em>Kein</em>
              </MenuItem>
              {authors.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.display_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Portale (Routing):
            </Typography>
            {sites.map((s) => (
              <FormControlLabel
                key={s.site_key}
                sx={{ display: 'block' }}
                control={
                  <Checkbox
                    checked={targetSites.includes(s.site_key)}
                    onChange={() => toggleSite(s.site_key)}
                  />
                }
                label={`${s.name} (${s.site_key})`}
              />
            ))}
          </Box>

          <TextField
            label="Notizen (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            minRows={2}
            size="small"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button variant="contained" onClick={save} disabled={saving || !canSave}>
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeedCreateDialog;
